import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { loadStripe, type Stripe } from '@stripe/stripe-js';
import dayjs from 'dayjs';

import { fetchAdminTeamInfo, fetchTeamSubPrice, previewTeamPlanPrice, currentTeamPlanPrice, type AdminTeamInfo, type TeamSubPrice } from '@/api/modules/team';

import { fetchStripeConfig } from '@/api/modules/subpayment';
import { fetchSystemUserInfo, hasActiveSubscription, isTeamSubYearly, readTeamCountry } from '@/api/modules/user';
import { DEFAULT_TEAM_COUNTRY, normalizeTeamCountry, teamCountryCurrency } from '@/constants/teamCountries';

import { SubscribePaymentForm } from '@/pages/subscribe/components/SubscribePaymentForm';
import { userFacingError } from '@/lib/userFacingError';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import { parseBackendDateTime } from '@/utils/parseBackendDateTime';


type BillingPeriod = 'monthly' | 'yearly';
type SubscribeMode = 'new' | 'continue' | 'renew' | 'add_seats' | 'change';



const PLAN_FEATURES = [

  'Unlimited work orders',

  'Team management & scheduling',

  'Real-time GPS tracking',

  'Digital work orders',

  'Priority support',

];



function formatPrice(value: number): string {

  const rounded = Math.round(value * 100) / 100;

  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);

}



function ExtraSeatsStepper({
  value,
  onChange,
  min = 0,
  unitPrice,
  currencySymbol,
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  unitPrice: number;
  currencySymbol: string;
}) {
  return (
    <div className="flex flex-col items-end justify-center min-w-[9.5rem]">
      <p className="text-gray-500 text-[10px] font-semibold uppercase tracking-widest">Extra team seats</p>
      <p className="text-green-700 text-[10px] font-semibold mt-1">
        {currencySymbol}{formatPrice(unitPrice)}/seat/mo
      </p>
      <div className="flex items-center gap-2 mt-2">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-200 text-gray-700 font-bold cursor-pointer disabled:opacity-40"
          aria-label="Decrease extra seats"
        >
          −
        </button>
        <div className="text-center w-8">
          <p className="text-gray-900 font-bold text-lg leading-none">{value}</p>
        </div>
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-200 text-gray-700 font-bold cursor-pointer"
          aria-label="Increase extra seats"
        >
          +
        </button>
      </div>
    </div>
  );
}

function CapacityBreakdown({
  used,
  currentLimit,
  adding = 0,
  extraControl,
}: {
  used: number;
  currentLimit: number;
  adding?: number;
  extraControl?: ReactNode;
}) {
  const nextLimit = Math.max(0, currentLimit + adding);
  const remainingNow = Math.max(0, currentLimit - used);
  const usedPct = nextLimit > 0 ? Math.min(100, Math.round((used / nextLimit) * 100)) : 0;
  const addPct =
    nextLimit > 0 && adding > 0 ? Math.min(100 - usedPct, Math.round((adding / nextLimit) * 100)) : 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 space-y-2.5">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-gray-500 text-[10px] font-semibold uppercase tracking-widest">Seat capacity</p>
          <p className="text-gray-900 font-bold text-2xl leading-none mt-1.5">
            {used}
            <span className="text-gray-400 font-semibold text-base"> / {nextLimit}</span>
          </p>
          <p className="text-gray-400 text-[10px] mt-1">used / plan capacity</p>
          {!extraControl && adding !== 0 && (
            <span
              className={`inline-block mt-1.5 text-[10px] font-bold rounded-full px-2 py-0.5 border ${
                adding > 0
                  ? 'text-green-700 bg-green-50 border-green-100'
                  : 'text-amber-700 bg-amber-50 border-amber-100'
              }`}
            >
              {adding > 0 ? `+${adding}` : adding} seats
            </span>
          )}
        </div>
        {extraControl ? (
          <div className="flex-shrink-0 pl-3 ml-1 border-l border-gray-100 self-stretch flex items-center">
            {extraControl}
          </div>
        ) : null}
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex">
        <div className={`h-full ${usedPct >= 100 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${usedPct}%` }} />
        {addPct > 0 && <div className="h-full bg-green-300" style={{ width: `${addPct}%` }} />}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-gray-50 rounded-lg px-2 py-1.5">
          <p className="text-gray-400 text-[10px]">Used now</p>
          <p className="text-gray-900 font-bold text-sm">{used}</p>
        </div>
        <div className="bg-gray-50 rounded-lg px-2 py-1.5">
          <p className="text-gray-400 text-[10px]">Current cap</p>
          <p className="text-gray-900 font-bold text-sm">{currentLimit}</p>
        </div>
        <div className="bg-gray-50 rounded-lg px-2 py-1.5">
          <p className="text-gray-400 text-[10px]">{adding !== 0 ? 'After change' : 'Invites left'}</p>
          <p className="text-gray-900 font-bold text-sm">{adding !== 0 ? nextLimit : remainingNow}</p>
        </div>
      </div>
    </div>
  );
}

function formatSubscriptionDate(raw?: string | null): string {
  if (!raw) return '—';
  const parsed = parseBackendDateTime(raw);
  if (!parsed) return raw;
  return dayjs(parsed).format('D MMM YYYY');
}

function resolveSubscriptionStatus(expireAt: string | null | undefined): 'none' | 'active' | 'expired' {
  if (!expireAt) return 'none';
  const parsed = parseBackendDateTime(expireAt);
  if (!parsed) return 'active';
  return parsed.getTime() > Date.now() ? 'active' : 'expired';
}

function CurrentSubscriptionSummary({
  loading,
  teamInfo,
  fallbackSeats,
  fallbackMembers,
  onViewOrders,
}: {
  loading: boolean;
  teamInfo: AdminTeamInfo | null;
  fallbackSeats: number;
  fallbackMembers: number;
  onViewOrders: () => void;
}) {
  const capacity = teamInfo?.member_capacity;
  const seats =
    capacity?.totalCapacity
    ?? (typeof teamInfo?.base_members === 'number'
      ? teamInfo.base_members + (teamInfo.extra_members ?? 0)
      : fallbackSeats);
  const members = teamInfo?.member_count ?? capacity?.currentCount ?? fallbackMembers;
  const expireAt = teamInfo?.expire_time || teamInfo?.subscription_info?.expire_time || null;
  const status = resolveSubscriptionStatus(expireAt);
  const trial = teamInfo?.is_trial_activated === 1 && status === 'active';
  const extraSeats = capacity?.extraMembers ?? teamInfo?.extra_members ?? 0;
  const baseSeats = capacity?.baseMembers ?? teamInfo?.base_members;
  const badge = trial
    ? { label: 'Trial', className: 'bg-amber-50 text-amber-700 border-amber-200' }
    : status === 'active'
      ? { label: 'Active', className: 'bg-green-50 text-green-700 border-green-200' }
      : status === 'expired'
        ? { label: 'Expired', className: 'bg-red-50 text-red-600 border-red-200' }
        : { label: 'Inactive', className: 'bg-gray-100 text-gray-500 border-gray-200' };
  const seatHint =
    typeof baseSeats === 'number' && baseSeats > 0
      ? extraSeats > 0
        ? `${baseSeats} included + ${extraSeats} extra`
        : `${baseSeats} included`
      : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <p className="text-gray-500 text-[10px] font-semibold uppercase tracking-widest">Current subscription</p>
            <p className="text-gray-900 font-bold text-sm mt-0.5">
              {trial ? 'Free trial' : status === 'none' ? 'No active plan' : 'Team plan'}
            </p>
          </div>
          {!loading && (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${badge.className}`}>
              {badge.label}
            </span>
          )}
        </div>

        {loading ? (
          <div className="space-y-1.5" aria-busy="true">
            {[0, 1, 2].map((key) => (
              <div key={key} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-1.5">
                <div className="h-2.5 bg-gray-200 rounded animate-pulse w-16" />
                <div className="h-3 bg-gray-200 rounded animate-pulse w-12" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3 bg-gray-50 rounded-md px-3 py-1.5">
              <p className="text-gray-500 text-xs">Expires</p>
              <p className="text-gray-900 font-semibold text-sm">{formatSubscriptionDate(expireAt)}</p>
            </div>
            <div className="flex items-center justify-between gap-3 bg-gray-50 rounded-md px-3 py-1.5">
              <p className="text-gray-500 text-xs">Seats</p>
              <p className="text-gray-900 font-semibold text-sm">
                {seats}
                {seatHint ? <span className="text-gray-400 text-[10px] font-medium ml-1.5">{seatHint}</span> : null}
              </p>
            </div>
            <div className="flex items-center justify-between gap-3 bg-gray-50 rounded-md px-3 py-1.5">
              <p className="text-gray-500 text-xs">Team members</p>
              <p className="text-gray-900 font-semibold text-sm">{members}</p>
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onViewOrders}
        className="w-full px-4 py-2.5 flex items-center gap-2.5 border-t border-gray-100 cursor-pointer hover:bg-gray-50 text-left"
      >
        <div className="w-7 h-7 flex items-center justify-center rounded-md bg-green-50 flex-shrink-0">
          <i className="ri-file-list-3-line text-green-600 text-sm" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-gray-800 text-xs font-medium">Subscription orders</div>
          <div className="text-gray-400 text-[11px] mt-0.5">Plan, extra seats, and trial payments</div>
        </div>
        <i className="ri-arrow-right-s-line text-gray-300 text-base flex-shrink-0" />
      </button>
    </div>
  );
}

function yearlySavingsPercent(priceMonth: number, priceYear: number): number | null {
  const fullYearMonthly = priceMonth * 12;
  if (fullYearMonthly <= 0 || priceYear >= fullYearMonthly) return null;
  return Math.round(((fullYearMonthly - priceYear) / fullYearMonthly) * 100);
}

export default function SubscribePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    mode?: string;
    extraMembers?: number;
    addCount?: number;
    billing?: BillingPeriod;
  } | null;
  const subscribeMode: SubscribeMode = (() => {
    const mode = locationState?.mode;
    if (mode === 'continue' || mode === 'renew' || mode === 'add_seats' || mode === 'change') {
      return mode;
    }
    return 'new';
  })();
  const initialExtraFromState =
    typeof locationState?.extraMembers === 'number' && Number.isFinite(locationState.extraMembers)
      ? Math.max(0, Math.floor(locationState.extraMembers))
      : null;

  const [prices, setPrices] = useState<TeamSubPrice | null>(null);
  const [priceCountry, setPriceCountry] = useState(DEFAULT_TEAM_COUNTRY);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [teamInfo, setTeamInfo] = useState<AdminTeamInfo | null>(null);
  const [teamInfoLoading, setTeamInfoLoading] = useState(true);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [stripeLoadError, setStripeLoadError] = useState<string | null>(null);
  const billingFromState =
    locationState?.billing === 'yearly' || locationState?.billing === 'monthly'
      ? locationState.billing
      : null;
  const [billing, setBilling] = useState<BillingPeriod>(billingFromState ?? 'monthly');
  const [showSheet, setShowSheet] = useState(false);
  const billingTouchedRef = useRef(Boolean(billingFromState));
  const [extraSeats, setExtraSeats] = useState(
    subscribeMode === 'add_seats'
      ? Math.max(1, typeof locationState?.addCount === 'number' ? locationState.addCount : 1)
      : 0,
  );
  const [seatsReady, setSeatsReady] = useState(
    subscribeMode !== 'continue' && subscribeMode !== 'renew' && subscribeMode !== 'change',
  );
  const openedFromState = useRef(false);




  useEffect(() => {

    let cancelled = false;



    (async () => {

      setLoading(true);

      setLoadError(null);

      try {

        let country = DEFAULT_TEAM_COUNTRY;
        try {
          const info = await fetchAdminTeamInfo();
          if (info.country?.trim()) country = normalizeTeamCountry(info.country);
        } catch {
          try {
            const profile = await fetchSystemUserInfo();
            const fromProfile = readTeamCountry(profile.team_info);
            if (fromProfile?.trim()) country = normalizeTeamCountry(fromProfile);
          } catch {
            country = DEFAULT_TEAM_COUNTRY;
          }
        }
        const data = await fetchTeamSubPrice({ country });

        if (!cancelled) {
          setPriceCountry(country);
          setPrices(data);
        }

      } catch (e) {

        if (!cancelled) {

          setLoadError(userFacingError(e, 'Failed to load prices. Please try again.'));

        }

      } finally {

        if (!cancelled) setLoading(false);

      }

    })();



    return () => {

      cancelled = true;

    };

  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTeamInfoLoading(true);
      try {
        const info = await fetchAdminTeamInfo();
        if (!cancelled) setTeamInfo(info);
      } catch {
        if (!cancelled) setTeamInfo(null);
      } finally {
        if (!cancelled) setTeamInfoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);



  useEffect(() => {

    let cancelled = false;



    (async () => {

      setStripeLoadError(null);

      try {
        const cfg = await fetchStripeConfig();
        const promise = loadStripe(cfg.publishableKey);
        if (!cancelled) {
          setStripePromise(promise);
        }
        const stripe = await promise;
        if (!cancelled && !stripe) {
          setStripeLoadError(
            'Stripe failed to load. Check your network connection and try again.',
          );
        }
      } catch (e) {
        if (!cancelled) {
          setStripePromise(null);
          setStripeLoadError(
            userFacingError(e, 'Failed to load Stripe. Check your network and try again.'),
          );
        }
      }

    })();



    return () => {

      cancelled = true;

    };

  }, []);



  const handleSelectPlan = (period: BillingPeriod) => {
    billingTouchedRef.current = true;
    setBilling(period);
    setShowSheet(true);
  };

  const handleToggleBilling = () => {
    billingTouchedRef.current = true;
    setBilling((prev) => (prev === 'yearly' ? 'monthly' : 'yearly'));
  };

  useEffect(() => {
    if (billingTouchedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const info = await fetchSystemUserInfo();
        if (cancelled || billingTouchedRef.current) return;
        billingTouchedRef.current = true;
        setBilling(isTeamSubYearly(info) ? 'yearly' : 'monthly');
        void hasActiveSubscription;
      } catch {
        billingTouchedRef.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);



  const handleCloseSheet = () => {
    setShowSheet(false);
  };

  useEffect(() => {
    if (!prices) return;
    if (subscribeMode === 'continue' || subscribeMode === 'renew') {
      setExtraSeats(prices.teamCapacity?.extraMembers ?? 0);
    } else if (subscribeMode === 'change') {
      setExtraSeats(initialExtraFromState ?? prices.teamCapacity?.extraMembers ?? 0);
    }
    setSeatsReady(true);
  }, [prices, subscribeMode, initialExtraFromState]);

  useEffect(() => {
    if (openedFromState.current || !prices || !stripePromise || !seatsReady) return;
    const preselect = locationState?.billing;
    if (preselect === 'monthly' || preselect === 'yearly') {
      openedFromState.current = true;
      setBilling(preselect);
      setShowSheet(true);
    }
  }, [prices, stripePromise, seatsReady, locationState?.billing]);

  const currentExtra = prices?.teamCapacity?.extraMembers ?? 0;
  const extraForPlan =
    subscribeMode === 'continue' || subscribeMode === 'renew' ? currentExtra : extraSeats;
  const preview = prices ? previewTeamPlanPrice(prices, extraForPlan) : null;
  const usedCount = prices?.teamCapacity?.currentCount ?? 0;
  const currentLimit = prices?.teamCapacity?.totalCapacity ?? prices?.baseMemberCount ?? 0;
  const addingSeats =
    subscribeMode === 'add_seats'
      ? extraSeats
      : subscribeMode === 'continue' || subscribeMode === 'renew'
        ? 0
        : extraSeats - currentExtra;

  const amountDue = (p: TeamSubPrice, period: BillingPeriod) => {
    if (subscribeMode === 'add_seats') {
      return p.extraMemberPriceMonth * extraSeats;
    }
    const next = previewTeamPlanPrice(p, extraForPlan);
    return period === 'monthly' ? next.month : next.year;
  };

  const billingLabel = (period: BillingPeriod) =>
    period === 'monthly' ? 'Monthly' : 'Yearly';

  const savings = preview ? yearlySavingsPercent(preview.month, preview.year) : null;
  const currencySymbol = teamCountryCurrency(priceCountry).symbol;
  const money = (value: number) => `${currencySymbol}${formatPrice(value)}`;

  const planOptions: { period: BillingPeriod; title: string; subtitle: string; highlight: boolean }[] =
    prices && preview
      ? [
          {
            period: 'monthly',
            title: 'Monthly',
            subtitle: `${money(preview.month)} / month`,
            highlight: false,
          },
          {
            period: 'yearly',
            title: 'Yearly',
            subtitle: `${money(preview.year)} / year`,
            highlight: true,
          },
        ]
      : [];

  const amountLabel = useMemo(() => {

    if (!billing || !prices) return '';

    const amt = formatPrice(amountDue(prices, billing));

    return `${currencySymbol}${amt}${billing === 'monthly' ? '/mo' : '/yr'}`;

  }, [billing, prices, extraSeats, subscribeMode, extraForPlan, currencySymbol]);



  return (

    <div lang="en" className="min-h-screen bg-[#f8f9fa] pb-6">

      <SubpageHeader
        title={
          subscribeMode === 'continue'
            ? 'Continue Subscription'
            : subscribeMode === 'renew'
              ? 'Renew Subscription'
              : subscribeMode === 'add_seats'
                ? 'Add Extra Seats'
                : subscribeMode === 'change'
                  ? 'Change Plan'
                  : 'Choose a Plan'
        }
        subtitle={
          subscribeMode === 'continue'
            ? 'Keep the same plan — new time starts after current expiry'
            : subscribeMode === 'renew'
              ? 'Reactivate your current team plan'
              : subscribeMode === 'add_seats'
                ? 'Keep this plan and buy extra member seats for the remaining period'
                : subscribeMode === 'change'
                  ? 'Pick extra seats and billing for the next period'
                  : 'Base plan includes team seats; add extras if you need more'
        }
        onBack={() => navigate('/me')}
      />



      <div className="px-4 pt-5 space-y-3">

        {loadError && (

          <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">

            {loadError}

          </div>

        )}



        {stripeLoadError && (

          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">

            {stripeLoadError}

          </div>

        )}

        <CurrentSubscriptionSummary
          loading={teamInfoLoading}
          teamInfo={teamInfo}
          fallbackSeats={prices?.teamCapacity?.totalCapacity ?? prices?.baseMemberCount ?? 0}
          fallbackMembers={prices?.teamCapacity?.currentCount ?? 0}
          onViewOrders={() => navigate('/subscribe/orders')}
        />

        {loading && !prices ? (

          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">

            <i className="ri-loader-4-line text-3xl text-green-500 animate-spin mb-3 block mx-auto" />

            <div className="text-sm text-gray-500 font-medium">Loading plans...</div>

          </div>

        ) : prices ? (
          <>
            <CapacityBreakdown
              used={usedCount}
              currentLimit={currentLimit || prices.baseMemberCount}
              adding={addingSeats}
              extraControl={
                subscribeMode === 'new' || subscribeMode === 'change' || subscribeMode === 'add_seats' ? (
                  <ExtraSeatsStepper
                    value={extraSeats}
                    onChange={setExtraSeats}
                    min={subscribeMode === 'add_seats' ? 1 : 0}
                    unitPrice={prices.extraMemberPriceMonth}
                    currencySymbol={currencySymbol}
                  />
                ) : undefined
              }
            />

            {(subscribeMode === 'continue' || subscribeMode === 'renew') && (
              <div className="bg-green-50 border border-green-100 rounded-lg px-4 py-3 text-xs text-green-800">
                Same plan: {usedCount} used · {currentLimit} current cap · adding 0
                {' · '}
                {money(currentTeamPlanPrice(prices).month)}/mo
              </div>
            )}

            {subscribeMode === 'add_seats' ? (
              <div className="bg-white rounded-lg border-2 border-green-500 p-3">
                <p className="text-gray-900 font-bold text-base mb-1">Pay for extra seats now</p>
                <p className="text-gray-400 text-xs mb-4">
                  Charged for the remaining months of the current subscription. Extra seats stay on future renewals.
                </p>
                <div className="flex items-end justify-between mb-4">
                  <span className="text-gray-500 text-sm">Estimated monthly add-on</span>
                  <span className="text-gray-900 font-bold text-2xl leading-none">
                    {money(prices.extraMemberPriceMonth * extraSeats)}
                    <span className="text-gray-400 text-xs font-medium">/mo</span>
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setBilling('monthly');
                    setShowSheet(true);
                  }}
                  disabled={extraSeats < 1 || !stripePromise}
                  className="w-full h-11 rounded-lg font-normal text-sm bg-green-500 text-white hover:bg-green-600 cursor-pointer disabled:opacity-50 flex items-center justify-center"
                >
                  Continue to payment
                </button>
              </div>
            ) : (
          planOptions.map((opt) => (

            <div

              key={opt.period}

              className={`bg-white rounded-lg border-2 overflow-hidden transition-all ${

                opt.highlight ? 'border-green-500' : 'border-gray-200'

              }`}

            >

              {opt.highlight && savings !== null && savings > 0 && (

                <div className="bg-green-500 px-4 py-1.5 flex items-center justify-center gap-1.5">

                  <i className="ri-fire-fill text-white text-xs" />

                  <span className="text-white text-xs font-bold tracking-wide uppercase">

                    Save {savings}% vs monthly

                  </span>

                </div>

              )}

              <div className="p-3">

                <div className="flex items-start justify-between mb-3">

                  <div>

                    <div className="flex items-center gap-2 mb-0.5">

                      <div

                        className={`w-7 h-7 flex items-center justify-center rounded-lg ${

                          opt.highlight ? 'bg-green-50' : 'bg-gray-50'

                        }`}

                      >

                        <i

                          className={`ri-leaf-line text-sm ${

                            opt.highlight ? 'text-green-600' : 'text-gray-500'

                          }`}

                        />

                      </div>

                      <span className="text-gray-900 font-bold text-base">{opt.title}</span>

                    </div>

                    <p className="text-gray-400 text-xs mt-1">

                      {opt.period === 'monthly'

                        ? 'Billed every month, cancel anytime'

                        : `Billed once per year - ${money((preview?.year ?? 0) / 12)}/mo equivalent`}

                    </p>

                  </div>

                  <div className="text-right flex-shrink-0 ml-4">

                    <div className="flex items-end gap-0.5 justify-end">

                      <span className="text-gray-400 text-sm font-medium">{currencySymbol}</span>

                      <span className="text-gray-900 font-bold text-3xl leading-none">

                        {formatPrice(
                          opt.period === 'monthly'
                            ? (preview?.month ?? 0)
                            : (preview?.year ?? 0),
                        )}

                      </span>

                    </div>

                    <span className="text-gray-400 text-xs">

                      / {opt.period === 'monthly' ? 'month' : 'year'}

                    </span>

                  </div>

                </div>



                <div className="space-y-2 mb-4">

                    {(prices
                      ? [
                          `${usedCount} used now · ${currentLimit || prices.baseMemberCount} current cap`,
                          addingSeats !== 0
                            ? `After this plan: ${currentLimit + addingSeats} seats (${addingSeats > 0 ? '+' : ''}${addingSeats})`
                            : `Seat limit stays at ${currentLimit || prices.baseMemberCount}`,
                          `Includes ${prices.baseMemberCount} team members`,
                          extraForPlan > 0
                            ? `${extraForPlan} extra seat${extraForPlan === 1 ? '' : 's'} (${money(prices.extraMemberPriceMonth)}/seat/mo)`
                            : `Extra seats ${money(prices.extraMemberPriceMonth)}/mo each`,
                          ...PLAN_FEATURES,
                        ]
                      : PLAN_FEATURES
                    ).map((text) => (

                    <div key={text} className="flex items-center gap-2.5">

                      <div className="w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 bg-green-100">

                        <i className="ri-check-line text-green-600 text-[10px]" />

                      </div>

                      <span className="text-xs text-gray-700">{text}</span>

                    </div>

                  ))}

                </div>



                <button

                  type="button"

                  onClick={() => handleSelectPlan(opt.period)}

                  disabled={!prices}

                  className={`w-full h-11 rounded-lg font-normal text-sm cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50 flex items-center justify-center ${

                    opt.highlight

                      ? 'bg-green-500 text-white hover:bg-green-600'

                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'

                  }`}

                >

                  {subscribeMode === 'continue'
                    ? `Continue — ${opt.title}`
                    : subscribeMode === 'renew'
                      ? `Renew — ${opt.title}`
                      : subscribeMode === 'change'
                        ? `Switch — ${opt.title}`
                        : `Subscribe — ${opt.title}`}

                </button>

              </div>

            </div>

          ))
            )}
          </>
        ) : null}



        <div className="bg-white rounded-lg border border-gray-200 p-3">

          <div className="grid grid-cols-3 gap-3">

            {[

              { icon: 'ri-shield-check-line', label: 'Secure\nPayment', color: 'text-green-600', bg: 'bg-green-50' },

              { icon: 'ri-loop-left-line', label: 'Cancel\nAnytime', color: 'text-teal-600', bg: 'bg-teal-50' },

              { icon: 'ri-customer-service-2-line', label: '24/7\nSupport', color: 'text-emerald-600', bg: 'bg-emerald-50' },

            ].map((badge) => (

              <div key={badge.label} className="flex flex-col items-center gap-1.5 text-center">

                <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${badge.bg}`}>

                  <i className={`${badge.icon} ${badge.color} text-base`} />

                </div>

                <span className="text-gray-500 text-[10px] font-medium whitespace-pre-line leading-tight">

                  {badge.label}

                </span>

              </div>

            ))}

          </div>

        </div>



        <p className="text-center text-gray-400 text-[10px] leading-relaxed px-2 pb-2">

          By subscribing you agree to our Terms of Service and Privacy Policy.

          Subscriptions auto-renew until cancelled.

        </p>

      </div>



      {showSheet && billing && prices && (

        <>

          <div

            className="fixed inset-0 bg-black/40 z-40"

            onClick={handleCloseSheet}

          />

          <div

            className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl max-h-[90vh] overflow-y-auto overscroll-contain"

            style={{ maxWidth: 480, margin: '0 auto' }}

            onClick={(e) => e.stopPropagation()}

          >

            <>

                <div className="pt-4 pb-2 flex justify-center">

                  <div className="w-10 h-1 bg-gray-200 rounded-full" />

                </div>



                <div className="px-4 pb-8">

                  <h2 className="text-gray-900 font-bold text-lg mb-4 text-center">
                    {subscribeMode === 'continue'
                      ? 'Confirm Continuation'
                      : subscribeMode === 'renew'
                        ? 'Confirm Renewal'
                        : subscribeMode === 'add_seats'
                          ? 'Confirm Extra Seats'
                          : subscribeMode === 'change'
                            ? 'Confirm Plan Change'
                            : 'Confirm Subscription'}
                  </h2>



                  <div className="bg-green-50 rounded-lg p-3 mb-4">

                    <div className="flex items-center justify-between mb-2">

                      <span className="text-sm font-bold text-green-600">

                        {billingLabel(billing)} Plan

                      </span>

                      <span className="text-gray-900 font-bold text-base">

                        {money(amountDue(prices, billing))}

                        <span className="text-gray-400 text-xs font-normal">

                          /{billing === 'monthly' ? 'mo' : 'yr'}

                        </span>

                      </span>

                    </div>

                    <div className="flex items-center gap-1.5">

                      <i className="ri-checkbox-circle-line text-green-500 text-xs" />

                      <span className="text-gray-500 text-xs">
                        {subscribeMode === 'continue'
                          ? 'New period stacks after your current expiry'
                          : subscribeMode === 'add_seats'
                            ? 'Seats apply to the remaining months of this period'
                            : subscribeMode === 'change'
                              ? 'Next period uses the new seat allowance'
                              : 'Full team subscription access'}
                      </span>

                    </div>

                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <div className="bg-white/70 rounded-lg px-2 py-1.5">
                        <p className="text-gray-400 text-[10px]">Used now</p>
                        <p className="text-gray-900 font-bold text-sm">{usedCount}</p>
                      </div>
                      <div className="bg-white/70 rounded-lg px-2 py-1.5">
                        <p className="text-gray-400 text-[10px]">Current cap</p>
                        <p className="text-gray-900 font-bold text-sm">{currentLimit}</p>
                      </div>
                      <div className="bg-white/70 rounded-lg px-2 py-1.5">
                        <p className="text-gray-400 text-[10px]">{addingSeats !== 0 ? 'After change' : 'Adding'}</p>
                        <p className="text-gray-900 font-bold text-sm">
                          {addingSeats !== 0 ? currentLimit + addingSeats : '+0'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 mt-1">

                      <i className="ri-loop-left-line text-green-500 text-xs" />

                      <span className="text-gray-500 text-xs">Cancel anytime, no commitment</span>

                    </div>

                  </div>



                  <div className="space-y-2 mb-5">

                    <div className="flex justify-between text-sm">

                      <span className="text-gray-500">

                        Team subscription ({billing})

                      </span>

                      <span className="text-gray-800 font-medium">

                        {money(amountDue(prices, billing))}

                      </span>

                    </div>

                    <div className="flex justify-between text-sm">

                      <span className="text-gray-500">Tax (0%)</span>

                      <span className="text-gray-800 font-medium">{money(0)}</span>

                    </div>

                    <div className="border-t border-gray-100 pt-2 flex justify-between">

                      <span className="text-gray-800 font-bold text-sm">Total today</span>

                      <span className="text-gray-900 font-bold text-sm">

                        {money(amountDue(prices, billing))}

                      </span>

                    </div>

                  </div>



                  {stripePromise ? (

                    <SubscribePaymentForm
                      billing={billing}
                      amountLabel={amountLabel}
                      stripePromise={stripePromise}
                      extraMembers={
                        subscribeMode === 'continue' || subscribeMode === 'renew'
                          ? undefined
                          : extraSeats
                      }
                      orderKind={subscribeMode === 'add_seats' ? 'extra_members' : 'subscription'}
                    />

                  ) : stripeLoadError ? (
                    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                      {stripeLoadError}
                    </div>
                  ) : (

                    <div className="text-center py-2.5 text-sm text-gray-500">

                      <i className="ri-loader-4-line animate-spin text-green-500 text-xl mb-2 block mx-auto" />

                      Loading payment...

                    </div>

                  )}



                  <p className="text-center text-gray-400 text-[10px] mt-3">

                    Protected by 256-bit SSL encryption

                  </p>

                </div>

            </>

          </div>

        </>

      )}

    </div>

  );

}

